import React, { useRef, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DocumentSelection } from '@/types';
import { DOCUMENT_NAME_MAX_LENGTH } from '@/constants/document-constants';

interface SelectedDocumentsPanelProps {
  documentSelections: Map<string, DocumentSelection>;
  onRemoveDocument: (docGuid: string) => void;
  onClearAll: () => void;
  matterSlug: string;
  className?: string;
  /**
   * Number of folders selected alongside `documentSelections`. When > 0 the
   * count is surfaced in the summary label (e.g. "1 folder, 2 documents
   * selected") so users see folder picks reflected in the footer even though
   * folders aren't rendered as chips.
   */
  selectedFolderCount?: number;
}

function buildSelectionSummary(folderCount: number, documentCount: number): string {
  const parts: string[] = [];
  if (folderCount > 0) {
    parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`);
  }
  if (documentCount > 0) {
    parts.push(`${documentCount} document${documentCount !== 1 ? 's' : ''}`);
  }
  return `${parts.join(', ')} selected`;
}

/**
 * Panel showing selected documents with deselect capability.
 * Displays as horizontal scrollable list of document chips.
 */
export const SelectedDocumentsPanel: React.FC<SelectedDocumentsPanelProps> = ({
  documentSelections,
  onRemoveDocument,
  onClearAll,
  matterSlug,
  className,
  selectedFolderCount = 0,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Convert vertical scroll to horizontal scroll
  // Using native event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  const selectedCount = documentSelections.size;

  if (selectedCount === 0 && selectedFolderCount === 0) {
    return null;
  }

  const summary = buildSelectionSummary(selectedFolderCount, selectedCount);

  const getDisplayName = (selection: DocumentSelection): string => {
    return selection.displayName || selection.fileName || 'Unknown';
  };

  const getTruncatedName = (name: string): string => {
    return name.length > DOCUMENT_NAME_MAX_LENGTH
      ? name.substring(0, DOCUMENT_NAME_MAX_LENGTH) + '...'
      : name;
  };

  const handleDocumentClick = (docGuid: string) => {
    window.open(`/matter/${matterSlug}/f/${docGuid}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={cn("", className)}>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-sm font-medium text-gray-700">
          {summary}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-gray-500 hover:text-gray-700 h-7 px-2"
        >
          Clear All
        </Button>
      </div>

      <div
        ref={scrollContainerRef}
        className="w-full overflow-x-auto pb-1"
      >
        <div className="flex gap-2 whitespace-nowrap">
          {Array.from(documentSelections.values()).map(selection => {
            const fullName = getDisplayName(selection);
            const truncatedName = getTruncatedName(fullName);

            return (
              <Badge
                key={selection.guid}
                variant="secondary"
                className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 hover:bg-gray-50 cursor-pointer shrink-0"
                title={fullName}
                onClick={() => handleDocumentClick(selection.guid)}
              >
                <FileText className="h-3 w-3 text-gray-500" />
                <span className="text-xs font-normal text-gray-700 hover:underline">
                  {truncatedName}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveDocument(selection.guid);
                  }}
                  className="ml-1 hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${fullName}`}
                >
                  <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                </button>
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SelectedDocumentsPanel;
